import {Button} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import {atoms} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {NewsPostWithRead} from 'client';
import {DSYS_RAIL_ROW_INSET_PX} from 'components/DSYS/layoutConstants';
import {Link} from 'components/DSYS/Link';
import {NewsRow} from 'components/news/NewsRow';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import {useLoaderData} from 'react-router';
import {useDraftsSuspense, useFeed} from 'views/news/hooks';

interface NewsListProps {
  isEditorView?: boolean;
  viewCategory?: string;
  selected?: number;
  // Full-bleed rails (Layout.Page): rows run the rail edge to edge, square, with
  // their content back on the rail's row inset, like WorkstreamTree's rootBleed.
  bleed?: {left: number; right: number};
}

export const NewsList = (props: NewsListProps) => {
  const [startingPosts] = useLoaderData() as [NewsPostWithRead[]];
  const [selectedNewsPostId, setSelectedNewsPostId] = useState<number | undefined>();
  const [showViewMore, setShowViewMore] = useState<boolean>(true);
  const drafts = useDraftsSuspense();

  const [posts, setPosts] = useState<NewsPostWithRead[]>(startingPosts);
  const [skip, setSkip] = useState(25);
  const limit = 25;
  const data = useFeed(skip, limit, props.viewCategory);

  const handleViewMore = () => {
    analyticsLogger().logEvent('NEWS_VIEW_MORE_CLICKED');
    if (data?.length) {
      setPosts([...posts, ...data]);
      setSkip(skip + limit);
    } else {
      setShowViewMore(false);
    }
  };

  useEffect(() => {
    setPosts(startingPosts);
  }, [startingPosts]);

  useEffect(() => {
    setSelectedNewsPostId(props.selected);
  }, [props.selected]);

  const feedPosts = props.isEditorView ? [...(drafts ?? []), ...posts] : posts;

  return (
    <List
      isSelectable
      className={atoms({borderRadius: 'XLarge', height: '0'})}
      // DIG's List pads its top 8px; in a rail the first row sits flush under the header, like RailTree's.
      style={props.bleed ? {paddingTop: 0} : undefined}
    >
      {feedPosts.map((newspost) => (
        <Link
          to={`/news/${newspost.id}`}
          state={{source: 'news-list'}}
          key={newspost.is_draft ? `link-draft_${newspost.id}` : `link-listitem_${newspost.id}`}
          hasNoUnderline
          style={
            props.bleed
              ? {display: 'block', marginLeft: -props.bleed.left, marginRight: -props.bleed.right}
              : {display: 'block', marginLeft: 3, marginRight: 3}
          }
        >
          <List.Item
            key={newspost.is_draft ? `draft_${newspost.id}` : `listitem_${newspost.id}`}
            isSelected={selectedNewsPostId === newspost.id}
            onClick={() => {
              setSelectedNewsPostId(newspost.id);
            }}
            className={
              props.bleed ? atoms({paddingY: '12'}) : atoms({padding: '12', borderRadius: 'Medium'})
            }
            style={
              props.bleed
                ? {
                    paddingLeft: DSYS_RAIL_ROW_INSET_PX + props.bleed.left,
                    paddingRight: DSYS_RAIL_ROW_INSET_PX + props.bleed.right,
                    borderRadius: 0,
                  }
                : undefined
            }
          >
            <NewsRow
              id={newspost.id}
              title={newspost.title}
              category={newspost.category}
              creationDate={newspost.created_at}
              is_read={
                selectedNewsPostId === newspost.id ||
                ('read' in newspost ? (newspost.read as boolean) : undefined)
              }
              is_draft={newspost.is_draft ?? false}
              postAt={newspost.post_at}
              isCategoryView={!!props.viewCategory}
              isPinned={newspost.pin_order !== null}
            />
          </List.Item>
        </Link>
      ))}
      {feedPosts.length >= 25 && showViewMore && (
        <Button
          style={{
            marginTop: '16px',
            marginBottom: '88px',
            marginLeft: '12px',
          }}
          variant="transparent"
          size="medium"
          onClick={handleViewMore}
        >
          {t('view_more')}
        </Button>
      )}
    </List>
  );
};
